import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Nav, Tab, Badge, Modal, Form } from "react-bootstrap";
import { collection, query, where, getDocs, doc, updateDoc, increment, onSnapshot } from "firebase/firestore";
import { auth, db } from "../Components/Firebase";
import { fetchVideoStats } from "../Components/fetchVideoStats";

// Cloudflare Stream Video Player Component
const AniSparkPlayer = ({ videoId, onPlay }) => (
  <iframe
    title="AniSpark Video Player"
    src={`https://iframe.videodelivery.net/${videoId}`}
    style={{ width: '100%', aspectRatio: '16/9', border: 'none', borderRadius: '0.5rem' }}
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
    onLoad={() => {
      // Track view when video loads
      if (onPlay) onPlay();
    }}
  />
);

// Cloudflare Stream Thumbnail Component
const VideoThumbnail = ({ videoId, title }) => (
  <img
    src={`https://videodelivery.net/${videoId}/thumbnails/thumbnail.jpg`}
    alt={title}
    style={{ 
      width: '100%', 
      aspectRatio: '16/9', 
      objectFit: 'cover', 
      borderRadius: '0.5rem 0.5rem 0 0' 
    }}
    onError={(e) => {
      // Fallback if thumbnail fails to load
      e.target.style.backgroundColor = '#374151';
      e.target.style.display = 'flex';
      e.target.style.alignItems = 'center';
      e.target.style.justifyContent = 'center';
      e.target.innerHTML = '🎬';
    }}
  />
);

const CreatorDashboard = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: '',
    tags: ''
  });

  // Track video view - Fixed version (no local state update)
  const trackView = async (episodeId) => {
    try {
      const episodeRef = doc(db, "episodes", episodeId);
      await updateDoc(episodeRef, {
        views: increment(1),
        lastViewed: new Date()
      });
      
      console.log(`View tracked for episode: ${episodeId}`);
    } catch (error) {
      console.error("Error tracking view:", error);
    }
  };

  // Function to refresh episodes data
  const refreshEpisodes = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const q = query(collection(db, "episodes"), where("creatorId", "==", user.uid));
      const querySnapshot = await getDocs(q);
      const episodesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch additional stats if needed
      const withStats = await Promise.all(
        episodesData.map(async (ep) => {
          try {
            const stats = await fetchVideoStats(ep.videoId);
            return { ...ep, ...stats };
          } catch (error) {
            console.warn("Could not fetch stats for", ep.videoId, error);
            return ep;
          }
        })
      );

      setEpisodes(withStats);
    } catch (error) {
      console.error("Error fetching episodes:", error);
    }
  };

  useEffect(() => {
    const fetchEpisodes = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Set up real-time listener for episodes
        const q = query(collection(db, "episodes"), where("creatorId", "==", user.uid));
        
        const unsubscribe = onSnapshot(q, async (querySnapshot) => {
          const episodesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Fetch additional stats if needed
          const withStats = await Promise.all(
            episodesData.map(async (ep) => {
              try {
                const stats = await fetchVideoStats(ep.videoId);
                return { ...ep, ...stats };
              } catch (error) {
                console.warn("Could not fetch stats for", ep.videoId, error);
                return ep;
              }
            })
          );

          setEpisodes(withStats);
          setLoading(false);
        });

        // Return cleanup function
        return () => unsubscribe();
      } catch (error) {
        console.error("Error setting up episodes listener:", error);
        setLoading(false);
      }
    };

    const unsubscribe = fetchEpisodes();
    
    // Cleanup on unmount
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // Add a refresh button handler
  const handleRefresh = () => {
    setLoading(true);
    refreshEpisodes().finally(() => setLoading(false));
  };

  // Handle edit modal
  const handleEdit = (episode) => {
    setEditingEpisode(episode);
    setFormData({
      title: episode.title || '',
      description: episode.description || '',
      genre: episode.genre || '',
      tags: episode.tags ? episode.tags.join(', ') : ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEpisode) return;

    try {
      const episodeRef = doc(db, "episodes", editingEpisode.id);
      const updatedData = {
        title: formData.title,
        description: formData.description,
        genre: formData.genre,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        title_lower: formData.title.toLowerCase(), // For search functionality
        updatedAt: new Date()
      };

      await updateDoc(episodeRef, updatedData);

      // Don't update local state here - let the real-time listener handle it
      
      setShowEditModal(false);
      setEditingEpisode(null);
    } catch (error) {
      console.error("Error updating episode:", error);
      alert("Error updating episode. Please try again.");
    }
  };

  // Calculate totals from episodes data
  const totalViews = episodes.reduce((sum, ep) => sum + (ep.views || 0), 0);
  const totalLikes = episodes.reduce((sum, ep) => sum + (ep.likes || 0), 0);
  const totalComments = episodes.reduce((sum, ep) => sum + (ep.comments || 0), 0);

  return (
    <Container fluid className="py-5" style={{ background: "linear-gradient(to bottom right, #1F1333, #3D1D5A)", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="text-white fw-bold display-5 mb-2">Creator Dashboard</h1>
          <p className="text-white-50 mb-0">Welcome back, Creator! Manage your anime content here.</p>
        </div>
        <Button 
          variant="outline-light" 
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh Data"}
        </Button>
      </div>

      <Tab.Container defaultActiveKey="overview">
        <Nav variant="pills" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="overview">Overview</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="content">My Content</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="analytics">Analytics</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="overview">
            <Row className="mb-4">
              <Col md={4}>
                <Card bg="dark" text="white" className="mb-3 shadow">
                  <Card.Body>
                    <Card.Title>Total Views</Card.Title>
                    <Card.Text className="display-6">{totalViews.toLocaleString()}</Card.Text>
                    <Card.Text className="text-success">↑ 12% from last month</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card bg="dark" text="white" className="mb-3 shadow">
                  <Card.Body>
                    <Card.Title>Total Likes</Card.Title>
                    <Card.Text className="display-6">{totalLikes.toLocaleString()}</Card.Text>
                    <Card.Text className="text-success">↑ 8% from last month</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card bg="dark" text="white" className="mb-3 shadow">
                  <Card.Body>
                    <Card.Title>Total Comments</Card.Title>
                    <Card.Text className="display-6">{totalComments.toLocaleString()}</Card.Text>
                    <Card.Text className="text-success">↑ 15% from last month</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <h4 className="text-white mb-3">Recent Uploads</h4>
            <Row>
              {loading ? (
                <Col md={12}>
                  <Card bg="dark" text="white" className="p-4">
                    <Card.Text>Loading your content...</Card.Text>
                  </Card>
                </Col>
              ) : (
                <>
                  {episodes.slice(0, 3).map((episode) => (
                    <Col md={4} key={episode.id} className="mb-4">
                      <Card bg="dark" text="white" className="h-100 shadow" style={{ borderRadius: '1rem' }}>
                        <AniSparkPlayer 
                          videoId={episode.videoId} 
                          onPlay={() => trackView(episode.id)}
                        />
                        <Card.Body>
                          <Card.Title>{episode.title || 'Untitled Episode'}</Card.Title>
                          <Card.Text className="text-white-50">
                            {episode.description?.substring(0, 100) || 'No description available'}
                            {episode.description?.length > 100 && '...'}
                          </Card.Text>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Badge bg="primary" className="me-1">{episode.views || 0} views</Badge>
                              <Badge bg="success">{episode.likes || 0} likes</Badge>
                            </div>
                            <Button 
                              variant="outline-light" 
                              size="sm"
                              onClick={() => handleEdit(episode)}
                            >
                              Edit
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                  <Col md={4} className="mb-4">
                    <Card className="h-100 d-flex align-items-center justify-content-center text-center border border-white border-2 border-dashed bg-transparent text-white" style={{ borderRadius: '1rem' }}>
                      <Card.Body>
                        <h5>＋ Upload New Anime</h5>
                        <p className="text-white-50">Share your creation with the world</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </>
              )}
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="content">
            <Card bg="dark" text="white" className="p-4" style={{ borderRadius: '1rem' }}>
              <Card.Title>My Content</Card.Title>
              {loading ? (
                <Card.Text className="text-white-50">Loading your content...</Card.Text>
              ) : episodes.length > 0 ? (
                <Row>
                  {episodes.map((episode) => (
                    <Col md={6} lg={4} key={episode.id} className="mb-4">
                      <Card bg="secondary" text="white" className="h-100 shadow" style={{ borderRadius: '1rem' }}>
                        <AniSparkPlayer 
                          videoId={episode.videoId} 
                          onPlay={() => trackView(episode.id)}
                        />
                        <Card.Body>
                          <Card.Title>{episode.title || 'Untitled Episode'}</Card.Title>
                          <Card.Text className="text-white-75">
                            <strong>Genre:</strong> {episode.genre || 'Not specified'}<br/>
                            <strong>Description:</strong> {episode.description?.substring(0, 80) || 'No description available'}
                            {episode.description?.length > 80 && '...'}
                          </Card.Text>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <Badge bg="info" className="me-1">{episode.views || 0} views</Badge>
                              <Badge bg="success" className="me-1">{episode.likes || 0} likes</Badge>
                              <Badge bg="warning">{episode.comments || 0} comments</Badge>
                            </div>
                            <Button 
                              variant="outline-light" 
                              size="sm"
                              onClick={() => handleEdit(episode)}
                            >
                              Edit
                            </Button>
                          </div>
                          <div className="mt-2">
                            <small className="text-white-50">
                              Uploaded: {episode.date?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                            </small>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Card.Text className="text-white-50">
                  No content uploaded yet. Start by uploading your first anime!
                </Card.Text>
              )}
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="analytics">
            <Card bg="dark" text="white" className="p-4" style={{ borderRadius: '1rem' }}>
              <Card.Title>Analytics</Card.Title>
              {loading ? (
                <Card.Text className="text-white-50">Loading analytics...</Card.Text>
              ) : (
                <>
                  <Card.Text className="text-white-50 mb-4">
                    Performance data and audience insights for your {episodes.length} uploaded episodes.
                  </Card.Text>
                  <Row>
                    <Col md={6}>
                      <h6 className="text-white">Top Performing Content</h6>
                      {episodes.length > 0 ? episodes
                        .sort((a, b) => (b.views || 0) - (a.views || 0))
                        .slice(0, 5)
                        .map((episode, index) => (
                          <div key={episode.id} className="d-flex justify-content-between align-items-center mb-2 p-3 bg-secondary rounded">
                            <div>
                              <span className="fw-bold">#{index + 1} {episode.title || 'Untitled'}</span>
                              <br/>
                              <small className="text-white-75">{episode.genre || 'No genre'}</small>
                            </div>
                            <div className="text-end">
                              <Badge bg="primary" className="d-block mb-1">{episode.views || 0} views</Badge>
                              <Badge bg="success">{episode.likes || 0} likes</Badge>
                            </div>
                          </div>
                        )) : (
                          <p className="text-white-50">No content available for analysis</p>
                        )}
                    </Col>
                    <Col md={6}>
                      <h6 className="text-white">Engagement Summary</h6>
                      <div className="bg-secondary p-3 rounded">
                        <p className="text-white-75 mb-2">
                          <strong>Average views per episode:</strong> {episodes.length > 0 ? Math.round(totalViews / episodes.length).toLocaleString() : 0}
                        </p>
                        <p className="text-white-75 mb-2">
                          <strong>Average likes per episode:</strong> {episodes.length > 0 ? Math.round(totalLikes / episodes.length).toLocaleString() : 0}
                        </p>
                        <p className="text-white-75 mb-2">
                          <strong>Average comments per episode:</strong> {episodes.length > 0 ? Math.round(totalComments / episodes.length).toLocaleString() : 0}
                        </p>
                        <p className="text-white-75 mb-0">
                          <strong>Total episodes:</strong> {episodes.length}
                        </p>
                      </div>
                      
                      <h6 className="text-white mt-4 mb-3">Genre Distribution</h6>
                      <div className="bg-secondary p-3 rounded">
                        {episodes.length > 0 ? [...new Set(episodes.map(ep => ep.genre).filter(Boolean))]
                          .map(genre => {
                            const count = episodes.filter(ep => ep.genre === genre).length;
                            return (
                              <div key={genre} className="d-flex justify-content-between mb-1">
                                <span className="text-white-75">{genre}</span>
                                <Badge bg="outline-primary">{count} episode{count !== 1 ? 's' : ''}</Badge>
                              </div>
                            );
                          }) : (
                            <p className="text-white-50 mb-0">No genre data available</p>
                          )}
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Edit Episode Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#1F1333', borderBottom: '1px solid #3D1D5A' }}>
          <Modal.Title className="text-white">Edit Episode</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1F1333', color: 'white' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                style={{ backgroundColor: '#374151', border: '1px solid #6B7280', color: 'white' }}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                style={{ backgroundColor: '#374151', border: '1px solid #6B7280', color: 'white' }}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Genre</Form.Label>
              <Form.Select
                value={formData.genre}
                onChange={(e) => setFormData({...formData, genre: e.target.value})}
                style={{ backgroundColor: '#374151', border: '1px solid #6B7280', color: 'white' }}
              >
                <option value="">Select a genre</option>
                <option value="Action">Action</option>
                <option value="Adventure">Adventure</option>
                <option value="Comedy">Comedy</option>
                <option value="Drama">Drama</option>
                <option value="Fantasy">Fantasy</option>
                <option value="Horror">Horror</option>
                <option value="Romance">Romance</option>
                <option value="Sci-Fi">Sci-Fi</option>
                <option value="Slice of Life">Slice of Life</option>
                <option value="Thriller">Thriller</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tags (comma separated)</Form.Label>
              <Form.Control
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="e.g. anime, action, adventure"
                style={{ backgroundColor: '#374151', border: '1px solid #6B7280', color: 'white' }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1F1333', borderTop: '1px solid #3D1D5A' }}>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CreatorDashboard;